import { NavLink, useLocation } from 'react-router-dom';
import { useUserControlsContext } from '../../contexts/UserControlsContext';
import { isProduction } from '../../lib/environment';
import { hasRequiredRole } from '../../lib/roles';

interface TabVisibility {
  roles: string[];
  canImpersonate: boolean;
}

type TabGroup = 'dual' | 'v4' | 'legacy';

interface Tab {
  key: string;
  label: string;
  route: string;
  exact?: boolean;
  group: TabGroup;
  visible: (p: TabVisibility) => boolean;
  children?: Tab[];
}

// Tab visibility buckets mirror the AppRouter route→role map exactly (U8):
// queue/dbos -> infra_operator, users -> user_admin,
// configuration -> config_editor OR flow_admin (flow_admin reaches the Ranker tab only), ingestion -> config_editor OR usage_viewer
// (its Logs tab is usage_viewer-backed, its other tabs config_editor-backed),
// usage -> usage_viewer, render -> infra_operator,
// skills/stack-sweep/testing(+children) -> qa_tooling, except demo-data which
// widens to qa_tooling OR user_admin OR config_editor (its actions span all
// three backend buckets — see the AppRouter route comment for detail),
// mapping keeps canImpersonate (already admin-wildcard-inclusive server-side).
export const ADMIN_TABS: Tab[] = [
  // Dual
  { key: 'queue', label: 'Queue', route: '/admin/queue', group: 'dual', visible: (p) => hasRequiredRole(p.roles, ['infra_operator']) },
  { key: 'mapping', label: 'Mapping', route: '/admin/mapping', group: 'dual', visible: (p) => p.canImpersonate },
  { key: 'dbos', label: 'DBOS', route: '/admin/dbos', group: 'dual', visible: (p) => hasRequiredRole(p.roles, ['infra_operator']) },
  { key: 'users', label: 'Users', route: '/admin/users', group: 'dual', visible: (p) => hasRequiredRole(p.roles, ['user_admin']) },
  // V4
  { key: 'configuration', label: 'Configuration', route: '/admin/configuration', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['config_editor', 'flow_admin']) },
  { key: 'ingestion', label: 'Ingestion', route: '/admin/ingestion', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['config_editor', 'usage_viewer']) },
  { key: 'usage', label: 'Usage', route: '/admin/usage', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['usage_viewer']) },
  { key: 'render', label: 'Render', route: '/admin/render', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['infra_operator']) },
  { key: 'skills', label: 'Skills', route: '/admin/skills', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) },
  { key: 'stack-sweep', label: 'Stack Sweep', route: '/admin/stack-sweep', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) },
  {
    key: 'testing', label: 'Testing', route: '/admin/testing', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']),
    children: [
      { key: 'demo-data', label: 'Demo Data', route: '/admin/testing/demo-data', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling', 'user_admin', 'config_editor']) },
      { key: 'health', label: 'Health', route: '/admin/testing/health', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) },
      { key: 'walker', label: 'Walker', route: '/admin/testing/walker', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) },
      { key: 'seed-architecture', label: 'Seed Architecture', route: '/admin/testing/seed-architecture', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) && !isProduction() },
      { key: 'story-pipeline', label: 'Story Pipeline', route: '/admin/testing/story-pipeline', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) && !isProduction() },
      { key: 'legacy-tests', label: 'Legacy Tests', route: '/admin/testing/legacy-tests', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) && !isProduction() },
      { key: 'arch-explorer', label: 'Arch Explorer', route: '/admin/testing/arch-explorer', group: 'v4', visible: (p) => hasRequiredRole(p.roles, ['qa_tooling']) },
    ],
  },
];

function isTabActive(tab: Tab, pathname: string): boolean {
  if (tab.children) {
    return tab.children.some((child) => pathname.startsWith(child.route));
  }
  return tab.exact ? pathname === tab.route : pathname.startsWith(tab.route);
}

export function AdminTabBar() {
  const { roles, canImpersonate } = useUserControlsContext();
  const { pathname } = useLocation();
  const vis: TabVisibility = { roles: roles ?? [], canImpersonate };
  const visibleTabs = ADMIN_TABS.filter((tab) => tab.visible(vis));

  // Find the active parent with children (for sub-tab row)
  const activeSection = visibleTabs.find((tab) => tab.children && isTabActive(tab, pathname));
  const visibleChildren = activeSection?.children?.filter((c) => c.visible(vis));

  return (
    <div className="sticky top-0 z-10 bg-bg-raised border-b border-border-secondary">
      <nav className="px-6 flex items-center gap-1 overflow-x-auto">
        {visibleTabs.map((tab, i) => {
          const isActive = isTabActive(tab, pathname);
          const showDivider = i > 0 && tab.group !== visibleTabs[i - 1].group;
          return (
            <span key={tab.key} className="flex items-center">
              {showDivider && <span className="w-px h-5 bg-bg-tertiary mx-2 shrink-0" />}
              <NavLink
                to={tab.route}
                className={`px-3 py-3 text-sm whitespace-nowrap border-b-2 ${
                  isActive
                    ? 'border-blueberry-500 text-content-primary font-medium'
                    : 'border-transparent text-icon-subtle hover:text-content-tertiary'
                }`}
              >
                {tab.label}
              </NavLink>
            </span>
          );
        })}
      </nav>
      {visibleChildren && visibleChildren.length > 0 && (
        <nav className="px-6 flex items-center gap-1 overflow-x-auto bg-bg-secondary border-t border-border-tertiary">
          {visibleChildren.map((child) => {
            const isActive = child.exact ? pathname === child.route : pathname.startsWith(child.route);
            return (
              <NavLink
                key={child.key}
                to={child.route}
                className={`px-3 py-2 text-xs whitespace-nowrap border-b-2 ${
                  isActive
                    ? 'border-blueberry-500 text-content-primary font-medium'
                    : 'border-transparent text-icon-subtle hover:text-content-tertiary'
                }`}
              >
                {child.label}
              </NavLink>
            );
          })}
        </nav>
      )}
    </div>
  );
}
